import axios, { AxiosError } from "axios";

import LOCAL_STORAGE from "@/constants/local-storage";
import { logSessionEvent } from "@/utils/session-log";

const apiBaseUrl: string = import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br";

export type RefreshOutcome =
  /** Renovação concluída: novos tokens já persistidos. */
  | { status: "success"; accessToken: string; refreshToken: string }
  /** Falha definitiva: o refresh token não serve mais, a sessão deve ser encerrada. */
  | { status: "invalid"; reason: string }
  /** Falha temporária (rede, timeout, 5xx): a sessão deve ser preservada. */
  | { status: "transient"; reason: string };

let inFlight: Promise<RefreshOutcome> | null = null;

const getStoredRefreshToken = (): string | null =>
  localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

/**
 * Classifica o erro do endpoint de refresh.
 *
 * Só é definitivo quando a API responde recusando o token (4xx). Rede, timeout,
 * abort e 5xx são transitórios e não podem derrubar uma sessão válida.
 */
const classifyError = (error: unknown): RefreshOutcome => {
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;

  if (!status) {
    return { status: "transient", reason: `sem resposta do servidor (${axiosError?.code ?? "erro de rede"})` };
  }

  if (status >= 500) {
    return { status: "transient", reason: `servidor indisponível (${status})` };
  }

  if (status === 408 || status === 429) {
    return { status: "transient", reason: `resposta temporária (${status})` };
  }

  return { status: "invalid", reason: `refresh token recusado (${status})` };
};

const performRefresh = async (refreshToken: string): Promise<RefreshOutcome> => {
  try {
    const response = await axios.post(`${apiBaseUrl}/api/v1/login/refreshtoken`, { refreshToken });

    const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data ?? {};

    if (!newAccessToken || !newRefreshToken) {
      return { status: "invalid", reason: "resposta de refresh sem tokens" };
    }

    localStorage.setItem(LOCAL_STORAGE.accessTokenKey, newAccessToken);
    localStorage.setItem(LOCAL_STORAGE.refreshTokenKey, newRefreshToken);

    return { status: "success", accessToken: newAccessToken, refreshToken: newRefreshToken };
  } catch (error) {
    return classifyError(error);
  }
};

/**
 * Renovação centralizada da sessão (single-flight).
 *
 * Todas as origens (interceptors, refresh proativo, boot da autenticação,
 * SignalR) compartilham a mesma promessa enquanto uma renovação está em curso,
 * evitando chamadas concorrentes com o mesmo refresh token.
 */
export const refreshSession = (): Promise<RefreshOutcome> => {
  if (inFlight) return inFlight;

  const refreshToken = getStoredRefreshToken();

  if (!refreshToken) {
    return Promise.resolve<RefreshOutcome>({ status: "invalid", reason: "sem refresh token" });
  }

  inFlight = performRefresh(refreshToken)
    .then((outcome) => {
      if (outcome.status !== "success") {
        logSessionEvent(`refresh ${outcome.status}: ${outcome.reason}`);
      }
      return outcome;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Compatibilidade com os fluxos que só precisam do novo access token. */
export const refreshAccessToken = async (): Promise<string | null> => {
  const outcome = await refreshSession();
  return outcome.status === "success" ? outcome.accessToken : null;
};

/** Exposto apenas para testes. */
export const __resetRefreshState = () => {
  inFlight = null;
};

export default refreshSession;
